import { useCallback, useEffect, useState } from 'react';
import type { DeviceDetail, Screen } from '@mantelpiece/shared';
import { api, type StoredSession } from '../lib/api';
import { viewerUrl } from '../lib/viewer';
import type { Guard } from '../pages/Device';

interface Props {
  readonly session: StoredSession;
  readonly device: DeviceDetail;
  readonly guard: Guard;
}

const formatDate = (ms: number): string =>
  new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });

const formatSeen = (ms: number | null): string => {
  if (ms === null) return 'never connected';
  const minutes = Math.round((Date.now() - ms) / 60_000);
  if (minutes < 2) return 'connected now';
  if (minutes < 60) return `last seen ${minutes} min ago`;
  if (minutes < 1440) return `last seen ${Math.round(minutes / 60)} h ago`;
  return `last seen ${formatDate(ms)}`;
};

export const ScreensTab = ({ session, device, guard }: Props) => {
  const [screens, setScreens] = useState<readonly Screen[]>([]);
  const [code, setCode] = useState('');
  const [label, setLabel] = useState('Living room TV');
  const [paired, setPaired] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const viewer = viewerUrl();

  const load = useCallback(
    () => guard(async () => setScreens((await api.listScreens(session.token, device.id)).screens)),
    [device.id, guard, session.token],
  );

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <section className="stack-loose">
      <div>
        <div className="section-head">
          <p className="eyebrow">Connect a screen</p>
        </div>
        <p className="note" style={{ marginBottom: '1rem' }}>
          Open the app on the TV, or the viewer link below on any other screen. It shows an
          eight-character code — type it here within five minutes and the slideshow starts on its
          own. More than one screen can show this frame; they all get the same photos.
        </p>
        {/* The other half of the note on the Add a frame section. */}
        <p className="note" style={{ marginBottom: '1rem' }}>
          That is what you want if the new screen should show <em>these</em> photos. If it should
          show a different set — a different room, a different family — make a new frame for it
          instead, from the frames list.
        </p>
        <div className="row">
          <label className="field" style={{ flex: '0 1 12rem' }}>
            <span>Code on screen</span>
            <input
              className="input"
              value={code}
              maxLength={12}
              autoCapitalize="characters"
              spellCheck={false}
              style={{ fontFamily: 'ui-monospace, monospace', letterSpacing: '0.16em' }}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
            />
          </label>
          <label className="field">
            <span>Name this screen</span>
            <input
              className="input"
              value={label}
              maxLength={40}
              onChange={(event) => setLabel(event.target.value)}
            />
          </label>
          <button
            type="button"
            className="button"
            disabled={code.replace(/[^0-9A-Z]/g, '').length !== 8}
            onClick={() =>
              void guard(async () => {
                const result = await api.pairScreen(session.token, device.id, code, label);
                setPaired(result.label);
                setCode('');
                await load();
              })
            }
          >
            Connect
          </button>
        </div>
        {paired && (
          <p className="alert" data-tone="ok" style={{ marginTop: '1rem' }}>
            {paired} is connected. The slideshow should start within a few seconds.
          </p>
        )}
      </div>

      <div>
        <div className="section-head">
          <p className="eyebrow">Viewer link</p>
        </div>
        <p className="note" style={{ marginBottom: '1rem' }}>
          Any browser opened here becomes a frame — a spare tablet, an old laptop, the phone in
          your hand. It shows a pairing code exactly as the television does, and once you type
          that code in above it joins this frame.
        </p>
        <div className="copyline">
          <code>{viewer}</code>
          <button
            type="button"
            className="button button-quiet button-small"
            onClick={() => {
              void navigator.clipboard.writeText(viewer).then(() => setCopied(true));
            }}
          >
            {copied ? 'Copied' : 'Copy'}
          </button>
        </div>
      </div>

      <div>
        <div className="section-head">
          <p className="eyebrow">Connected screens</p>
        </div>
        {screens.length === 0 ? (
          <p className="empty">No screens connected to this frame yet.</p>
        ) : (
          <ul className="ledger">
            {screens.map((screen) => (
              <li key={screen.id}>
                <span className="ledger-main">
                  <span className="ledger-name">{screen.label}</span>
                  <span className="ledger-meta">{formatSeen(screen.lastSeenAt)}</span>
                </span>
                <button
                  type="button"
                  className="button button-danger button-small"
                  onClick={() =>
                    void guard(async () => {
                      await api.removeScreen(session.token, device.id, screen.id);
                      await load();
                    })
                  }
                >
                  Disconnect
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};
